/** Mehrspieler: Raumübersicht, Raum, Verbindungsstatus und Servermeldungen. */
import type { mp as en } from '../en/mp';
import type { Translated } from '../types';

export const mp: Translated<typeof en> = {
  'mp.kicker': 'Fernmeldenetz · Mehrspieler',
  'mp.title': 'Zum Kriegsrat',
  'mp.commander': 'Befehlshaber',
  'mp.commander.hint': 'Für die anderen Spieler sichtbar. Wird auf diesem Rechner gespeichert.',
  'mp.commander.placeholder': 'z. B. Gen. Hartmann',
  'mp.status.online': 'VERBUNDEN',
  'mp.status.onlinePing': 'VERBUNDEN · {ping} ms',
  'mp.status.connecting': 'LEITUNG WIRD GESCHALTET …',
  'mp.status.offline': 'GETRENNT',
  'mp.connect': 'Verbinden',
  'mp.disconnect': 'Trennen',
  'mp.openRooms': 'Offene Räume',
  'mp.refresh': 'Aktualisieren',
  'mp.noLine.title': 'Keine Leitung zum Hauptquartier',
  'mp.noLine.text': 'Namen eingeben und verbinden, um die Räume im Fernmeldenetz zu sehen.',
  'mp.empty.title': 'Der Kriegsrat ist leer',
  'mp.empty.text': 'Einen Raum eröffnen und die Kennung aus fünf Buchstaben an die Gegner schicken.',
  'mp.col.code': 'Kennung',
  'mp.col.room': 'Raum',
  'mp.col.players': 'Spieler',
  'mp.col.theatre': 'Schauplatz',
  'mp.col.state': 'Stand',
  'mp.state.lobby': 'Im Aufbau',
  'mp.state.running': 'Im Gefecht',
  'mp.state.finished': 'Beendet',
  'mp.join': 'Beitreten',
  'mp.rejoin': 'Zurückkehren',
  'mp.joinByCode': 'Per Kennung beitreten',
  'mp.code.aria': 'Raumkennung',
  'mp.joinRoom': 'Raum betreten',
  'mp.newRoom': 'Neuen Raum eröffnen',
  'mp.roomName': 'Name des Raums',
  'mp.roomName.default': 'Der Krieg von {name}',
  'mp.roomName.placeholder': 'Die Varnischen Marken',
  'mp.theatre': 'Schauplatz',
  'mp.create': 'Raum eröffnen',
  'mp.hostNote':
    'Sie werden Gastgeber: Sie legen die Regeln fest, besetzen Plätze mit der KI und eröffnen den Krieg.',

  // ---- Raum
  'lobby.kicker': 'Kriegsrat · {n} Nationen',
  'lobby.kickerPing': 'Kriegsrat · {n} Nationen · {ping} ms',
  'lobby.leave': 'Verlassen',
  'lobby.code.copy': 'Raumkennung kopieren',
  'lobby.code.label': 'Raum',
  'lobby.code.copied': 'Kopiert',
  'lobby.invite': 'Einladungslink kopieren',
  'lobby.invite.copied': 'Link kopiert',
  'lobby.ctl.open': 'Offen',
  'lobby.ctl.ai': 'Computer',
  'lobby.ctl.closed': 'Geschlossen',
  'lobby.commander': 'Befehlshaber',
  // Badge next to the host's name: the slot's name column has room for a short word only.
  'lobby.host': 'LEITUNG',
  'lobby.human': 'Mensch',
  'lobby.dropped': 'Verbindung verloren — die KI führt',
  'lobby.computer': 'Computer',
  'lobby.openSlot': 'Freier Platz',
  'lobby.closedSlot': 'Geschlossen',
  'lobby.waitingCommander': 'Wartet auf einen Befehlshaber',
  'lobby.notInPlay': 'Nicht im Spiel',
  'lobby.changeColour': 'Farbe wechseln',
  'lobby.player': 'Spieler',
  'lobby.takeSlot': 'Platz einnehmen',
  'lobby.take': 'Einnehmen',
  'lobby.moveHere': 'Auf diesen Platz wechseln',
  'lobby.ready': 'Bereit',
  'lobby.waiting': 'Wartet',
  'lobby.standingBy': 'In Bereitschaft',
  'lobby.rules': 'Regeln',
  'lobby.rulesByHost': 'Regeln · vom Gastgeber festgelegt',
  'lobby.roomName': 'Name des Raums',
  'lobby.theatre': 'Schauplatz',
  'lobby.seed': 'Kartennummer',
  'lobby.dayLimit': 'Frist in Tagen',
  'lobby.speed': 'Tempo',
  'lobby.decisive': 'Entscheidender Sieg',
  'lobby.decisiveVal': '{pct} SP · 24 Std.',
  'lobby.decisiveRo': '{pct} aller Siegpunkte, 24 Stunden gehalten',
  'lobby.season': 'Jahreszeit',
  'lobby.techs': 'Technologie zu Beginn',
  'lobby.chat.placeholder': 'Nachricht an den Kriegsrat …',
  'lobby.chat.send': 'Senden',
  'lobby.belligerents': 'Kriegsparteien',
  'lobby.chatter': 'Aus dem Kriegsrat',
  'lobby.needTwo': 'Es braucht mindestens zwei Nationen.',
  'lobby.oneTeam': 'Alle stehen im selben Bündnis.',
  'lobby.notReady': 'Warten, bis alle Spieler Bereitschaft melden.',
  'lobby.hostNote':
    'Einladungslink oder Kennung weitergeben. Sie eröffnen den Krieg, sobald alle bereit sind.',
  'lobby.guestNote': 'Der Gastgeber eröffnet den Krieg, sobald alle Bereitschaft gemeldet haben.',
  'lobby.notReadyBtn': 'Nicht bereit',
  'lobby.reportReady': 'Bereitschaft melden',
  'lobby.start': 'Krieg eröffnen',
  'lobby.scenario': 'Szenario',
  'lobby.scenario.none': 'Keines — freie Aufstellung',
  'lobby.scenario.hint':
    'Eine Lage nach historischem Vorbild: Sie bestimmt Schauplatz, Jahreszeit, Bedingungen und die Kräfte jeder Seite.',
  'lobby.scenario.fixed': 'Vom Szenario vorgegeben',
  'lobby.scn.lead': 'Seite des Befehls',
  'lobby.scn.leadHint': 'Für diese Seite ist der Befehl geschrieben.',
  'lobby.scn.outside': 'Nicht im Szenario',
  'lobby.scn.ffa': 'Jede Nation für sich',

  'net.reconnecting': 'Fernmeldeleitung gestört — Verbindung zum Hauptquartier wird wiederhergestellt …',

  // ---- Fehler des Clients
  'err.version': 'Dieser Client ist älter als der Server. Bitte die Seite neu laden.',
  'err.replaced': 'Sie haben sich aus einem anderen Fenster oder Tab verbunden.',
  'err.rateLimited': 'Getrennt: zu viele Nachrichten.',
  'err.tooBig': 'Getrennt: Eine Nachricht war zu groß.',
  'err.policy': 'Vom Server getrennt.',
  'err.lost': 'Die Verbindung zum Server ist abgerissen.',
  'err.lostReason': 'Die Verbindung zum Server ist abgerissen ({reason}).',
  'err.unreachable': 'Der Server ist nicht erreichbar.',
  'err.invalidUrl': 'Ungültige Serveradresse',
  'err.simStopped': 'Die Simulation ist stehen geblieben: {detail}',
  'err.spError': 'Fehler im Einzelspieler: {detail}',
  'err.spCrashed': 'Die Einzelspieler-Engine ist abgestürzt: {detail}',
  'err.spUnreadable': 'Die Einzelspieler-Engine hat eine unlesbare Nachricht geschickt.',
  'err.replay': 'Die Aufzeichnung ließ sich nicht abspielen.',
  'err.replayCrashed': 'Die Wiedergabe-Engine ist abgestürzt.',

  // ---- Servermeldungen
  'srv.hello': 'Bitte zuerst anmelden.',
  'srv.notInRoom': 'Sie sind in keinem Raum.',
  'srv.greeted': 'Bereits angemeldet.',
  'srv.busy': 'Gerade werden zu viele Kriege geführt. Bitte später erneut versuchen.',
  'srv.full': 'Der Server ist voll. Bitte später erneut versuchen.',
  'srv.noRoom': 'Kein Raum mit dieser Kennung.',
  'srv.begun': 'Dieser Krieg hat bereits begonnen.',
  'srv.theBegun': 'Der Krieg hat bereits begonnen.',
  'srv.roomFull': 'Der Raum ist voll.',
  'srv.noSlot': 'Diesen Platz gibt es nicht.',
  'srv.hostOnlySlots': 'Nur der Gastgeber kann fremde Plätze ändern.',
  'srv.hostOnlyControl': 'Nur der Gastgeber kann die Besetzung eines Platzes ändern.',
  'srv.takeYourself': 'Spieler nehmen freie Plätze selbst ein.',
  'srv.occupied': 'Dieser Platz ist besetzt.',
  'srv.colour': 'Diese Farbe ist schon vergeben.',
  'srv.notOpen': 'Dieser Platz ist nicht frei.',
  'srv.noSeat': 'Sie haben in diesem Raum keinen Platz.',
  'srv.tooQuick': 'Sie senden zu schnell Nachrichten.',
  'srv.twoSides': 'Ein Krieg braucht mindestens zwei Seiten.',
  'srv.sameTeam': 'Alle Seiten stehen im selben Bündnis.',
  'srv.notConnected': '{name} ist nicht verbunden.',
  'srv.notReady': '{name} ist nicht bereit.',
  'srv.aPlayer': 'Ein Spieler',
  'srv.generate': 'Der Server konnte diesen Krieg nicht erzeugen. Bitte eine andere Kartennummer wählen.',
  'srv.noWar': 'Hier wird kein Krieg geführt.',
  'srv.gone': 'Diesen Raum gibt es nicht mehr.',
  'srv.hostOnly': 'Das darf nur der Gastgeber.',
  'srv.otherWindow': 'Sie haben sich aus einem anderen Fenster verbunden.',
  'srv.internal': 'Interner Serverfehler.',
  'srv.aborted': 'Die Partie wurde wegen eines Serverfehlers abgebrochen.',
  'srv.outdated': 'Ihr Client ist älter als dieser Server. Bitte die Seite neu laden.',
  'srv.scnSides': 'Dieses Szenario hat {n} Seiten.',
  'srv.scnPlayed': 'Jede Seite des Szenarios muss besetzt sein.',
  'srv.scnAlliances': 'Das Szenario legt die Bündnisse fest.',
  'srv.scnRoom': 'Dieses Szenario bietet Platz für {n} Befehlshaber.',
  'srv.mapSides': 'Diese Karte hat {n} Nationen.',
  'srv.mapPlayed': 'Jede Nation dieser Karte muss besetzt sein.',
  'srv.mapRoom': 'Diese Karte bietet Platz für {n} Befehlshaber.',
  'srv.observeAlive': 'Nur eine Nation, die kapituliert hat, kann den Krieg verfolgen.',
  'srv.observeAlliance': 'In einem Bündniskrieg behält eine besiegte Nation ihre eigene Lagekarte.',
  'srv.noSeatWar': 'Sie haben in diesem Krieg keinen Platz.',
  'srv.noPerspective': 'Diese Sicht gibt es nicht.',
  'srv.code.version': 'Der Client ist veraltet. Bitte die Seite neu laden.',
  'srv.code.serverFull': 'Der Server ist voll. Bitte später erneut versuchen.',
  'srv.code.notFound': 'Nicht gefunden.',
  'srv.code.roomFull': 'Der Raum ist voll.',
  'srv.code.forbidden': 'Nicht erlaubt.',
  'srv.code.state': 'Im Augenblick nicht möglich.',
  'srv.code.invalid': 'Ungültige Anfrage.',
  'srv.code.rateLimited': 'Zu viele Anfragen — bitte langsamer.',
  'srv.code.internal': 'Serverfehler.',
  'chat.joined': '{name} ist eingetreten.',
  'chat.left': '{name} ist gegangen.',
  'chat.removed': '{name} wurde entfernt.',
  'chat.lost': '{name} hat die Verbindung verloren.',
  'chat.back': '{name} ist wieder verbunden.',
  'chat.begun': 'Der Krieg hat begonnen.',
  'chat.over': 'Der Krieg ist aus.',
  'chat.overVictory': 'Der Krieg ist aus. Sieg: {names}.',
  'chat.aborted': 'Die Partie wurde wegen eines Serverfehlers abgebrochen.',
  'chat.newHost': '{name} ist jetzt Gastgeber.',
  'cmd.rejected': 'Befehl nicht angenommen — {reason}',
  'cmd.noUnits': 'keine Verbände ausgewählt',
  'cmd.maxUnits': 'höchstens {n} Verbände je Befehl',
  'cmd.outside': 'Ziel außerhalb der Karte',
  'cmd.impassable': 'Ziel ist unpassierbar',
  'cmd.hHour': 'ungültige X-Zeit',
  'cmd.noRadio': 'Feldfunk noch nicht eingeführt',
  'cmd.notYours': 'Verband {id} untersteht nicht unserem Befehl',
  'cmd.noHq': 'kein Hauptquartier und keine Stadt, von der Befehle ausgehen könnten',
  'cmd.requires': 'erfordert {tech}',
  'cmd.notBuildable': 'kann nicht aufgestellt werden',
  'cmd.cityLost': 'Stadt nicht mehr in unserer Hand',
  'cmd.queueFull': 'Auftragsbuch voll',
  'cmd.manpower': 'nicht genug Ersatz',
  'cmd.researched': 'bereits eingeführt',
  'cmd.over': 'der Krieg ist entschieden',
  'cmd.capitulated': 'unsere Nation hat kapituliert',
  'cmd.noItem': 'diesen Auftrag gibt es nicht',
  'cmd.other': 'vom Host abgelehnt',
};
